import * as React from "react";
import styled from "styled-components";
import { FieldWrapper } from "../components/FieldWrapper";
import { BaseField } from "./BaseField";
import { isHiddenInPdf } from "./configurables/HideInPdf";

const LegendWrapper = styled.div`
    page-break-before: always;
    break-before: page;
    margin-top: 20px;
`;

const LegendTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    th,
    td {
        border: 1px solid #d5d5d5;
        padding: 6px 8px;
        vertical-align: top;
        text-align: left;
    }

    th {
        background: #f2f4f7;
        font-weight: 600;
    }
`;

const LegendTitle = styled.h3`
    font-size: 16px;
    margin: 0 0 12px 0;
`;

interface ILegendItem {
    id?: number | null;
    shortName: string;
    name: string;
    description: string;
}

export class LegendeField extends BaseField {
    public name = "legende";
    public label = "Legende";
    public icon = "list";
    public fieldType = "Legende";

    public getConfigurables() {
        return [];
    }

    public EditComponent({ designer, schema, register, field }) {
        return (
            <FieldWrapper register={register} field={field} schema={schema} designer={designer}>
                Hier lässt sich nichts bearbeiten.
            </FieldWrapper>
        );
    }

    public InputComponent({ schema, saveState = {}, rawValue, variables = {} }: any) {
        const fields = (() => {
            if (schema && typeof schema.fields === "function") {
                return schema.fields();
            }
            if (schema && Array.isArray(schema.fields)) {
                return schema.fields;
            }
            return [];
        })();

        const legendItemsMap = new Map<string, ILegendItem>();

        const addItem = (item: ILegendItem) => {
            if (!item.shortName) {
                return;
            }
            const key = item.id ? `id:${item.id}` : `short:${item.shortName}`;
            if (!legendItemsMap.has(key)) {
                legendItemsMap.set(key, item);
            }
        };

        const parseValues = (value) => {
            if (!value) {
                return [];
            }
            if (Array.isArray(value)) {
                return value;
            }
            if (typeof value === "string") {
                try {
                    const parsed = JSON.parse(value);
                    if (Array.isArray(parsed)) {
                        return parsed;
                    }
                } catch (e) {
                    return value
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean);
                }
            }
            return [];
        };

        const parseTableData = (tableValue) => {
            if (!tableValue) {
                return [];
            }
            if (Array.isArray(tableValue)) {
                return tableValue;
            }
            if (typeof tableValue === "string") {
                try {
                    const parsed = JSON.parse(tableValue);
                    if (Array.isArray(parsed)) {
                        return parsed;
                    }
                } catch (e) {
                    return [];
                }
            }
            return [];
        };

        const isInventorySelectField = (field) => {
            return (
                field?.fieldType === "LeistungsV" ||
                field?.type === "inventory_item_select" ||
                field?.rawField?.type === "InventoryItemSelectField"
            );
        };

        const isInventoryTableRow = (subfield) => {
            return (
                subfield?.type === "inventory_item_select" ||
                subfield?.fieldType === "LeistungsV" ||
                subfield?.rawField?.type === "InventoryItemSelectTableRow"
            );
        };

        fields.forEach((field) => {
            if (variables?.isPrinting && isHiddenInPdf(field)) {
                return;
            }

            if (isInventorySelectField(field)) {
                const raw = saveState[field.hashCode] ?? rawValue;
                const values = parseValues(raw);
                values.forEach((val: any) => {
                    const cellData = val?.cell || val;
                    const shortName =
                        typeof val === "string"
                            ? val
                            : cellData?.short_name ||
                              cellData?.value ||
                              cellData?.name ||
                              cellData?.label ||
                              "";
                    const name =
                        typeof val === "object"
                            ? cellData?.name || cellData?.label || shortName
                            : shortName;
                    const description = typeof val === "object" ? cellData?.description || "" : "";

                    addItem({
                        id: typeof cellData?.id === "number" ? cellData.id : null,
                        shortName,
                        name,
                        description,
                    });
                });
            }

            if (field?.type === "TableField") {
                const tableValue = saveState[field.hashCode];
                const tableData = parseTableData(tableValue);
                const subfields = field.subfields || [];
                const inventoryItemColumnIndex = subfields.findIndex((subfield) =>
                    isInventoryTableRow(subfield)
                );

                if (inventoryItemColumnIndex === -1) {
                    return;
                }

                tableData.forEach((row) => {
                    if (!Array.isArray(row)) {
                        return;
                    }

                    const columnData = row[inventoryItemColumnIndex];
                    if (!columnData) {
                        return;
                    }

                    const rawValues = columnData?.value ?? columnData;
                    const values = Array.isArray(rawValues)
                        ? rawValues
                        : rawValues
                        ? [rawValues]
                        : [];

                    values.forEach((val: any) => {
                        if (!val) {
                            return;
                        }
                        const cellData = val?.cell || val;
                        const shortName =
                            typeof val === "string"
                                ? val
                                : cellData?.short_name ||
                                  cellData?.value ||
                                  cellData?.name ||
                                  cellData?.label ||
                                  "";
                        const name =
                            typeof val === "object"
                                ? cellData?.name || cellData?.label || shortName
                                : shortName;
                        const description =
                            typeof val === "object" ? cellData?.description || "" : "";

                        addItem({
                            id: typeof cellData?.id === "number" ? cellData.id : null,
                            shortName,
                            name,
                            description,
                        });
                    });
                });
            }
        });

        const legendItems = Array.from(legendItemsMap.values());

        if (legendItems.length === 0) {
            return <></>;
        }

        return (
            <LegendWrapper>
                <LegendTitle>Legende</LegendTitle>
                <LegendTable>
                    <thead>
                        <tr>
                            <th>Protokollansicht</th>
                            <th>Angebotsansicht</th>
                            <th>Beschreibung</th>
                        </tr>
                    </thead>
                    <tbody>
                        {legendItems.map((item) => (
                            <tr key={`${item.id || "short"}-${item.shortName}`}>
                                <td>{item.shortName}</td>
                                <td>{item.name || item.shortName}</td>
                                <td>{item.description || ""}</td>
                            </tr>
                        ))}
                    </tbody>
                </LegendTable>
            </LegendWrapper>
        );
    }

    public WrapComponent({ component }) {
        return <>{component}</>;
    }
}
